import { fromNullable, Maybe } from 'maybeasy';
import { learningCollectionsStore } from '../../../../../../stores/LearningCollectionsStore';
import { ClientExperienceResource, ScheduledProgram } from '../Types';

/**
 * Retrieves scheduled date IDs from the store for a given experience.
 * Uses the store data which always has the full scheduled dates,
 * consistent with the EDIT flow's initializeScheduledPrograms behavior.
 */
export function getScheduledDateIdsFromStore(experienceId: number): number[] {
  if (learningCollectionsStore.state.kind !== 'ready') {
    return [];
  }

  const experience = learningCollectionsStore.state.data.resource.payload.experiences.find(
    (exp) => exp.payload.id === experienceId,
  );

  if (!experience) {
    return [];
  }

  return experience.payload.program_instance_options.scheduled.map((date) => date.id);
}

/**
 * Creates a scheduled program entry with all dates selected.
 * Returns Nothing if the experience has no scheduled dates.
 */
export function createScheduledProgramEntry(
  experienceResource: ClientExperienceResource,
): Maybe<ScheduledProgram> {
  const experienceId = experienceResource.payload.id;
  const scheduledDateIds = getScheduledDateIdsFromStore(experienceId);

  if (scheduledDateIds.length === 0) {
    return fromNullable<ScheduledProgram>(null);
  }

  return fromNullable<ScheduledProgram>({
    id: experienceId,
    program_ids: scheduledDateIds,
  });
}

/**
 * Creates scheduled program entries for all experiences in the given sections.
 * Only creates entries for experiences with scheduled dates.
 * Preserves existing entries and avoids duplicates.
 */
export function createScheduledProgramsForSections(
  experienceIds: number[],
  existingScheduledPrograms: ScheduledProgram[],
): ScheduledProgram[] {
  const existingIds = new Set(existingScheduledPrograms.map((sp) => sp.id));

  const newEntries = experienceIds
    .filter((id) => !existingIds.has(id))
    .map((experienceId) => {
      const dateIds = getScheduledDateIdsFromStore(experienceId);
      if (dateIds.length === 0) {
        return null;
      }
      return {
        id: experienceId,
        program_ids: dateIds,
      };
    })
    .filter((entry): entry is ScheduledProgram => entry !== null);

  return [...existingScheduledPrograms, ...newEntries];
}
